import React, { useState, useEffect, useRef, useContext } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import "./header.scss";
import logo from "../../Assets/Images/Home/Logo.png";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import { Container, IconButton } from "@mui/material";
import { useAppContext } from "../../helpers/AppContext";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { AppStateContext } from "../../App";
import baseURL from "../../config/config";

export default function Header() {
  const { handleScroll, handleRegisterFormClick, handleTextbookHelpClick } = useAppContext();
  const [showNavLinks, setShowNavLinks] = useState(false);
  const [isScrolledUp, setIsScrolledUp] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [dropdownData, setDropdownData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const dropdownRef = useRef(null);
  const { access_token, user_type } = useContext(AppStateContext);
  const location = useLocation();

  const toggleNavLinks = () => {
    setShowNavLinks(!showNavLinks);
  };

  const handleLogoClick = () => {
    setShowNavLinks(false);
    setActiveDropdown(null);
    handleScroll();
  };


  const handleDropdownToggle = (name) => {
    setActiveDropdown(activeDropdown === name ? null : name);
  };

  const handleExpandMoreClick = (event, name) => {
    event.stopPropagation();
    handleDropdownToggle(name);
  };

  const handleRegisterFormItemClick = () => {
    setActiveDropdown(null);
    handleRegisterFormClick();
  };

  const handleOutsideClick = (event) => {
    if (!event.target.closest(".dropdown")) {
      setActiveDropdown(null);
    }
  };

  const handleDropdownItemClick = (url) => {
    setActiveDropdown(null);
    setShowNavLinks(false);
    
    // Open URL in a new window/tab
    if (url) {
      if (url.startsWith('http')) {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        // For relative URLs, construct absolute URL
        const absoluteUrl = window.location.origin + (url.startsWith('/') ? url : '/' + url);
        window.open(absoluteUrl, '_blank', 'noopener,noreferrer');
      }
    }
  };

  // Fetch dropdown data from API
  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${baseURL}/api/core/registeration-dropdown/`);
        const data = await response.json();
        
        if (data.status === "success" && data.data && data.data.results) {
          setDropdownData(data.data.results);
        }
      } catch (error) {
        console.error('Error fetching dropdown data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDropdownData();
  }, []);

  // Handle window resize for grouping products
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const shouldGroupProducts = windowWidth > 882 && windowWidth < 1450;

  let navLinks = [
    { to: "/", label: "Home" },
    ...(shouldGroupProducts
      ? [
          {
            label: "Products",
            dropdown: true,
            isProductDropdown: true,
            items: [
              { to: "/read-aloud", label: "Read Aloud", className: "read-aloud" },
              { to: "/textbook-help", label: "Textbook Help", className: "textbook-help" },
              { to: "/classroom-hub", label: "Classroom Hub", className: "classroom-activity" },
            ],
          },
        ]
      : [
          { to: "/read-aloud", label: "Read Aloud" },
          { to: "/textbook-help", label: "Textbook Help" },
          { to: "/classroom-hub", label: "Classroom Hub" },
        ]),
    { to: "/announcements", label: "Announcements" },

    { label: "Register", dropdown: true, isRegisterDropdown: true },
    ...(access_token && user_type === "admin"
      ? [{ to: "/admin-dashboard", label: "Admin Login" }]
      : []),
    ...(access_token && user_type === "student"
      ? [{ to: "/student-home", label: "Student Login" }]
      : []),
    ...(access_token && user_type === "teacher"
      ? [{ to: "/teacher-home", label: "Teacher Login" }]
      : []),
    
    { to: `${baseURL}/contact`, label: "Contact Us" },

        ...(!access_token || !user_type
      ? [{
          to: "/login",
          label: "Login",
          highlighted: true,
        }]
      : []),
    
  ];

  const handleNavLinkClick = () => {
    setShowNavLinks(false);
    setActiveDropdown(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolledUp(window.scrollY > 0);
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    document.addEventListener("click", handleOutsideClick);
    return () => {
      document.removeEventListener("click", handleOutsideClick);
    };
  }, []);

  return (
    <Container maxWidth="xl">
      <div className={`header ${isScrolledUp ? "scrolled" : ""}`}>
        <Link to={baseURL} onClick={handleLogoClick}>
          <img src={logo} alt="logo" />
        </Link>
        <IconButton
          className="nav-toggle"
          onClick={toggleNavLinks}
          color="primary">
          {showNavLinks ? (
            <CloseIcon onClick={() => setActiveDropdown(null)} />
          ) : (
            <MenuIcon />
          )}
        </IconButton>
        <ul className={`nav-links ${showNavLinks ? "show" : ""}`}>
          {navLinks.map(({ to, label, dropdown, highlighted, customStyle, items, isProductDropdown, isRegisterDropdown }) =>
            dropdown ? (
              <li
                key={label}
                className={`dropdown ${activeDropdown === label ? "d-show" : ""}`}>
                <span onClick={() => handleDropdownToggle(label)}>
                  {label}{" "}
                  {activeDropdown === label ? (
                    <ExpandLessIcon onClick={(e) => handleExpandMoreClick(e, label)} />
                  ) : (
                    <ExpandMoreIcon onClick={(e) => handleExpandMoreClick(e, label)} />
                  )}
                </span>
                {activeDropdown === label && (
                  <ul className="dropdown-menu">
                    {isRegisterDropdown ? (
                      loading ? (
                        <li className="dropdown-item">Loading...</li>
                      ) : dropdownData.length > 0 ? (
                        dropdownData.map((item) => (
                          <li
                            key={item.id}
                            className="dropdown-item"
                            onClick={() => handleDropdownItemClick(item.url)}
                          >
                            {item.label}
                          </li>
                        ))
                      ) : (
                        <li className="dropdown-item">No items available</li>
                      )
                    ) : isProductDropdown ? (
                      items.map((item) => (
                        <li
                          key={item.to}
                          className={`dropdown-item ${item.className || ""}`}
                          onClick={handleNavLinkClick}
                        >
                          <NavLink to={item.to} activeClassName="active">
                            {item.label}
                          </NavLink>
                        </li>
                      ))
                    ) : null}
                  </ul>
                )}
              </li>
            ) : (
              <li key={to} onClick={handleNavLinkClick}>
                <NavLink
                  exact={to === "/"}
                  to={to}
                  activeClassName="active"
                  className={
                    highlighted
                      ? "bg-orange-500 text-white px-4 py-2 rounded font-medium hover:bg-orange-600 transition-colors shadow-md hover:shadow-lg"
                      : ""
                  }
                  style={{ ...(highlighted ? { color: "white" } : {}), ...(customStyle || {}) }}>
                  {label}
                </NavLink>
              </li>
            )
          )}
        </ul>
      </div>
    </Container>
  );
} 